import { Paper, TablePagination } from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import {
	AdditionalInfoFieldType,
	AdditionalInfoType,
	ReceivingCondition,
	Severity,
	TestHierarchyNodeType,
	TestStatus,
	TurnaroundTime,
	WeightUnit,
} from "node-library/dist/constants";
import {
	FrontendCompanySampleAdditionalInfoFields,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestPackage,
	FrontendSample,
	FrontendSampleAdditionalInfoMapping,
	FrontendSampleInstance,
	FrontendSampleInstanceAdditionalInfoMapping,
	FrontendSampleSpecification,
	ParsedImportSampleInstanceRow,
	ParsedImportSampleRow,
} from "node-library/dist/portal";
import { ChangeEvent, DragEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton, GreyButton } from "../../../../common-components/button";
import { CustomDrawer } from "../../../../common-components/drawers/drawer";
import { StepsBar } from "../../../../common-components/steps-bar";
import {
	DeleteIcon,
	EditIcon,
	UploadIcon,
} from "../../../../common-components/svgIcons";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";
import * as XLSX from "xlsx";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import {
	normalizeHeader,
	parseBoolean,
	valueToString,
} from "../../../../helpers/importHelper";
import { createUUID } from "node-library/dist/helpers/request_helper";
import { deepClone } from "node-library/dist/helpers/misc_helper";
import { getFileDisplayName } from "node-library/dist/helpers/file_helper";

interface Props {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	selectedTestCategory: FrontendCompanyTestCategory;
	organizationSamples: FrontendSample[];
	organizationSpecifications: Array<FrontendSampleSpecification>;

	updateSampleInstancesFromImport: (
		newSamples: Array<FrontendSample>,
		sampleInstances: Array<FrontendSampleInstance>,
	) => void;
}

/** Same sub-matrix label logic as `editSampleModal` sub-matrix dropdown */
function getSubMatrixDisplayTitle(m: FrontendCompanySampleMatrix): string {
	const split = m.subMatrix.split(`${m.matrix} -`);
	return split.length > 1 ? split[1].trim() : m.subMatrix;
}

function findMatchingSampleMatrix(
	row: ParsedImportSampleRow,
	sampleMatrices: FrontendCompanySampleMatrix[],
): FrontendCompanySampleMatrix | undefined {
	if (row.isCustomMatrix) return undefined;
	const matrixKey = row.matrix.trim().toLowerCase();
	const subKey = row.subMatrix.trim().toLowerCase();
	if (!matrixKey || !subKey) return undefined;
	const candidates = sampleMatrices.filter(
		(m) => m.matrix.trim().toLowerCase() === matrixKey,
	);
	for (const m of candidates) {
		const displayTitle = getSubMatrixDisplayTitle(m).trim().toLowerCase();
		const fullSub = m.subMatrix.trim().toLowerCase();
		if (subKey === fullSub || subKey === displayTitle) {
			return m;
		}
	}
	return undefined;
}

export default function ImportOrderModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		open,
		setOpen,
		selectedTestCategory,
		organizationSamples,
		organizationSpecifications,
	} = props;
	const [loading, setLoading] = useState<boolean>(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	/** Row index in `parsedRows` → validation error message */
	const [dragging, setDragging] = useState<boolean>(false);

	const ACCEPTED_EXTENSIONS: string[] = [".csv", ".xls", ".xlsx"];

	//INFORMATION: SAMPLES
	const SAMPLES_REQUIRED_HEADERS: string[] = ["Lot Code"];
	// const SAMPLES_OPTIONAL_HEADERS: string[] = [
	// 	"Sample Name",
	// 	"Matrix",
	// 	"Sub Matrix",
	// 	"Sample Description",
	// ];
	const sampleAdditionalFields: FrontendCompanySampleAdditionalInfoFields[] =
		selectedTestCategory.sampleAdditionalInfo.filter(
			(field) => field.infoType === AdditionalInfoType.SAMPLE,
		);

	const sampleMandatoryAdditionalFields: FrontendCompanySampleAdditionalInfoFields[] =
		sampleAdditionalFields.filter((field) => field.mandatory);

	//INFORMATION: INSTANCE
	const SAMPLES_INSTANCE_REQUIRED_HEADERS: string[] = [
		"Weight",
		"Weight Unit",
		"Turnaround Time",
		"Due Date",
		"Receiving Condition",
		"Retest",
		"Additional Test",
	];
	// const SAMPLES_INSTANCE_OPTIONAL_HEADERS: string[] = ["Received Time"];
	const instanceAdditionalFields: FrontendCompanySampleAdditionalInfoFields[] =
		selectedTestCategory.sampleAdditionalInfo.filter(
			(field) => field.infoType === AdditionalInfoType.SAMPLE_INSTANCE,
		) || [];

	const instanceMandatoryAdditionalFields: FrontendCompanySampleAdditionalInfoFields[] =
		instanceAdditionalFields.filter((field) => field.mandatory);

	//INFORMATION: TESTS
	const categoryTestNodesOrOptionGroups: Array<FrontendCompanyTestHierarchyNode> =
		selectedTestCategory.hierarchyNodes.filter((node) => {
			return (
				node.nodeType === TestHierarchyNodeType.TEST ||
				node.nodeType === TestHierarchyNodeType.TEST_OPTION_RADIO_GROUP ||
				node.nodeType === TestHierarchyNodeType.TEST_GROUP_OPTION_RADIO_GROUP ||
				node.nodeType === TestHierarchyNodeType.TEST_GROUP_OPTION_CHECKBOX_GROUP
			);
		});
	// const TEST_OPTIONAL_HEADERS: string[] = [
	// 	"Treatment",
	// 	"Test Package",
	// 	"Other Testing",
	// 	"Other Instructions",
	// ];

	const REQUIRED_HEADERS = [
		...SAMPLES_REQUIRED_HEADERS,
		...SAMPLES_INSTANCE_REQUIRED_HEADERS,
	];
	const allAdditionalFields = [
		...sampleAdditionalFields,
		...instanceAdditionalFields,
	];

	useEffect(() => {
		if (open) {
			setSelectedFile(null);
		}
	}, [open]);

	async function parseAndSetFile(file: File): Promise<
		| {
				newSamples: Array<FrontendSample>;
				sampleInstances: Array<FrontendSampleInstance>;
		  }
		| undefined
	> {
		try {
			setLoading(true);
			const currentTime: Date = new Date();
			const extension = file.name.toLowerCase().split(".").pop() || "";
			if (!ACCEPTED_EXTENSIONS.includes(`.${extension}`)) {
				setSelectedFile(null);

				openSnackbar(
					translation("InvalidFile"),
					Severity.ERROR,
					undefined,
					<RedErrorIcon />,
				);
				return;
			}

			const data = await file.arrayBuffer();
			const workbook = XLSX.read(data, { type: "array" });
			const firstSheetName = workbook.SheetNames[0];
			if (!firstSheetName) {
				throw new Error("No worksheet found");
			}
			const sheet = workbook.Sheets[firstSheetName];
			const rawRows: unknown[][] = XLSX.utils.sheet_to_json(sheet, {
				header: 1,
				defval: "",
				raw: false,
				blankrows: false,
			});
			const rowCount = rawRows.length - 1; // Subtract 1 for header
			const ROW_LIMIT = 60;
			if (rowCount == 0) {
				throw new Error("No data rows found");
			}

			if (rowCount > ROW_LIMIT) {
				setSelectedFile(null);

				openSnackbar(
					// You can use a translation key like translation("RowLimitExceeded", { limit: 500 })
					translation("TooManySamplesToImport", {
						rowCount: rowCount,
						rowLimit: ROW_LIMIT,
					}),
					Severity.ERROR,
					undefined,
					<RedErrorIcon />,
				);
				return; // Stop execution
			}

			const headers: Array<string> = rawRows[0].map((header, index) =>
				normalizeHeader(header, index),
			);

			const headerAliasMap: Record<
				string,
				keyof ParsedImportSampleInstanceRow
			> = {
				samplename: "sampleName",
				lotcode: "lotCode",
				matrix: "matrix",
				submatrix: "subMatrix",
				sampledescription: "sampleDescription",

				weight: "weight",
				weightunit: "weightUnit",
				turnaroundtime: "turnaroundTime",
				duedate: "dueDate",
				receivingcondition: "receivingCondition",
				retest: "retest",
				additionaltest: "additionalTest",
				receivedtime: "receivedTime", // From optional headers

				treatment: "treatment",
				testpackage: "testPackage",
				otherTests: "otherTests",
				otherInstructions: "otherInstructions",
				specification: "specification",
			};

			const headerIndexMap: Partial<
				Record<keyof ParsedImportSampleInstanceRow, number>
			> = {};

			headers.forEach((header, index) => {
				const normalizedHeaderWithoutUnderscore = header.replace(/_/g, "");
				const directMatch = headerAliasMap[header];
				const relaxedMatch = headerAliasMap[normalizedHeaderWithoutUnderscore];
				const matchingCanonical = directMatch || relaxedMatch;
				if (matchingCanonical) {
					headerIndexMap[matchingCanonical] = index;
				}
			});

			//also read the additional infos if exists

			const additionalSampleFieldHeaderMap: Record<
				number,
				FrontendCompanySampleAdditionalInfoFields
			> = {}; // Index -> FieldId
			const additionalInstanceFieldHeaderMap: Record<
				number,
				FrontendCompanySampleAdditionalInfoFields
			> = {};

			const testHierarchyNodeHeaderMap: Record<
				number,
				FrontendCompanyTestHierarchyNode
			> = {};
			const hierarchyTestGroupNodeKeyAndChildrenMap: Record<
				number,
				Array<FrontendCompanyTestHierarchyNode>
			> = {};

			headers.forEach((header, index) => {
				// Normalize header to compare with field names
				const cleanHeader = header.replace(/_/g, "").toLowerCase();

				const matchingField:
					| FrontendCompanySampleAdditionalInfoFields
					| undefined = allAdditionalFields.find(
					(field) =>
						field.additionalFieldName.replace(/\s+/g, "").toLowerCase() ===
						cleanHeader,
				);

				if (matchingField) {
					if (matchingField.infoType === AdditionalInfoType.SAMPLE) {
						additionalSampleFieldHeaderMap[index] = matchingField;
					} else {
						additionalInstanceFieldHeaderMap[index] = matchingField;
					}
				} else {
					//check test node info
					const matchingNode: FrontendCompanyTestHierarchyNode | undefined =
						categoryTestNodesOrOptionGroups.find((node) => {
							return (
								node.nodeKey.replace(/\s+/g, "").toLowerCase() === cleanHeader
							);
						});
					if (matchingNode) {
						switch (matchingNode.nodeType) {
							case TestHierarchyNodeType.TEST_GROUP_OPTION_RADIO_GROUP:
							case TestHierarchyNodeType.TEST_GROUP_OPTION_CHECKBOX_GROUP:
							case TestHierarchyNodeType.TEST_OPTION_RADIO_GROUP:

							case TestHierarchyNodeType.TEST_ADDITIONAL_OPTION_GROUP:
								const childNodes: Array<FrontendCompanyTestHierarchyNode> =
									selectedTestCategory.hierarchyNodes.filter((node) => {
										return node.parentNodeId == matchingNode.nodeId;
									});
								hierarchyTestGroupNodeKeyAndChildrenMap[index] = childNodes;

								break;
							case TestHierarchyNodeType.TEST:
								testHierarchyNodeHeaderMap[index] = matchingNode;
								break;

							case TestHierarchyNodeType.TEST_GROUP:
							case TestHierarchyNodeType.ROOT:
							case TestHierarchyNodeType.RADIO_OPTION:
							case TestHierarchyNodeType.CHECKBOX_OPTION:
								//shouldn't happen, skip
								break;
						}
					}
				}
			});

			const missingHeaderLabels = REQUIRED_HEADERS.filter((headerLabel) => {
				const normalized = normalizeHeader(headerLabel, 0);
				const canonical =
					headerAliasMap[normalized] ||
					headerAliasMap[normalized.replace(/_/g, "")];
				return !canonical || headerIndexMap[canonical] === undefined;
			});
			if (missingHeaderLabels.length > 0) {
				setSelectedFile(null);

				openSnackbar(
					translation(`Missing headers: ${missingHeaderLabels.join(", ")}`),
					Severity.ERROR,
					undefined,
					<RedErrorIcon />,
				);
				return;
			}

			const orgSamplesCopy: Array<FrontendSample> =
				deepClone(organizationSamples);
			const newSamples: Array<FrontendSample> = [];

			// 2. Map the rows
			const sampleInstances: FrontendSampleInstance[] = [];

			let index: number = 0;
			for (const row of rawRows.slice(1)) {
				// parse sample info
				const lotCode: string = valueToString(
					row[headerIndexMap.lotCode as number],
				);
				const matchingSamples: Array<FrontendSample> =
					organizationSamples.filter((sample) => {
						return sample.lotCode === lotCode;
					});
				let multipleMatchingSamplesWarning: boolean = false;
				let missingAdditionalInfoWarning: boolean = false;

				let resultSample: FrontendSample;
				if (matchingSamples.length > 0) {
					//existing sample, use original info
					resultSample = matchingSamples[0];
					multipleMatchingSamplesWarning =
						matchingSamples.length > 1 ? true : false;
				} else {
					//new sample, parse full info

					const parsed: ParsedImportSampleRow = {
						rowNumber: index,
						sampleName: valueToString(row[headerIndexMap.sampleName as number]),
						lotCode,
						matrix: valueToString(row[headerIndexMap.matrix as number]),
						subMatrix: valueToString(row[headerIndexMap.subMatrix as number]),
						isCustomMatrix: false,
						sampleDescription: valueToString(
							row[headerIndexMap.sampleDescription as number],
						),
						additionalInfo: {}, // Initialize
					};

					const match = findMatchingSampleMatrix(
						parsed,
						selectedTestCategory.sampleMatrices,
					);
					if (match) parsed.matrixId = match.matrixId;
					else {
						openSnackbar(
							translation(
								`Unidentified matrix: ${parsed.matrix} - ${parsed.subMatrix} at row ${index + 2}`,
							),
							Severity.ERROR,
							undefined,
							<RedErrorIcon />,
						);
						return;
					}

					resultSample = {
						sampleId: createUUID(),
						lotCode,
						testCategoryId: selectedTestCategory.testCategoryId,
						sampleName: parsed.sampleName,
						sampleDescription: parsed.sampleDescription,
						matrix: "",
						matrixId: parsed.matrixId,
						sampleAdditionalInfos: [],

						isCustomMatrix: false,
						isNewSample: true,
					};
					// Extract values for any dynamic columns found
					Object.keys(additionalSampleFieldHeaderMap).forEach((colIndex) => {
						const field: FrontendCompanySampleAdditionalInfoFields =
							additionalSampleFieldHeaderMap[Number(colIndex)];

						const rawValue = valueToString(row[Number(colIndex)]);

						// Determine the final value based on the field type
						let finalValue: string | Date = rawValue;

						if (field.fieldType === AdditionalInfoFieldType.DATE && rawValue) {
							const parsedDate = new Date(rawValue);
							// Ensure the date is valid before assigning it, otherwise fallback to string
							finalValue = isNaN(parsedDate.getTime()) ? "" : parsedDate;
						}

						resultSample.sampleAdditionalInfos.push({
							fieldId: field.fieldId,
							infoType: field.infoType,
							additionalFieldName: field.additionalFieldName,
							fieldType: field.fieldType,
							value: finalValue,
						});
					});

					orgSamplesCopy.push(resultSample);
					newSamples.push(resultSample);
				}
				//validate mandatory sample additional infos
				for (const mandatoryField of sampleMandatoryAdditionalFields) {
					const existingEntry: FrontendSampleAdditionalInfoMapping | undefined =
						resultSample.sampleAdditionalInfos.find(
							(info) => info.fieldId === mandatoryField.fieldId,
						);

					// 1. Check if the entry exists at all
					if (!existingEntry) {
						missingAdditionalInfoWarning = true;
						break;
					}

					const val = existingEntry.value;

					// 2. Validate based on type
					let isValueEmpty = false;

					if (val instanceof Date) {
						// Check if date is "Invalid Date"
						isValueEmpty = isNaN(val.getTime());
					} else if (typeof val === "string") {
						// Check if string is just whitespace or empty
						isValueEmpty = val.trim().length === 0;
					} else {
						// Handle null/undefined if your types allow it
						isValueEmpty = !val;
					}

					if (isValueEmpty) {
						missingAdditionalInfoWarning = true;
						break;
					}
				}
				//instance info
				const receivedTime: Date = headerIndexMap.receivedTime
					? new Date(valueToString(row[headerIndexMap.receivedTime as number]))
					: currentTime;
				const receiveingCondition: ReceivingCondition =
					headerIndexMap.receivingCondition
						? (valueToString(
								row[headerIndexMap.receivingCondition as number],
							) as ReceivingCondition)
						: ReceivingCondition.AMBIENT;

				const isRetest: boolean = parseBoolean(
					valueToString(row[headerIndexMap.retest as number]),
				);
				const isAdditionalTest: boolean = parseBoolean(
					valueToString(row[headerIndexMap.additionalTest as number]),
				);
				//instance additional info and tests are handled at the end

				//additional test infos
				//check package, if exists
				const testPackage: string = headerIndexMap.testPackage
					? valueToString(row[headerIndexMap.testPackage as number])
					: "";

				const matchingPackage: FrontendCompanyTestPackage | undefined =
					selectedTestCategory.testPackages.find((pack) => {
						return pack.packageKey == testPackage;
					});
				const treatment: boolean = parseBoolean(
					valueToString(row[headerIndexMap.treatment as number]),
				);
				const otherInstructions: string = headerIndexMap.otherInstructions
					? valueToString(row[headerIndexMap.otherInstructions as number])
					: "";
				const otherTests: string = headerIndexMap.otherTests
					? valueToString(row[headerIndexMap.otherTests as number])
					: "";

				//check specification
				const specificationName: string = headerIndexMap.specification
					? valueToString(row[headerIndexMap.specification as number])
					: "";

				let matchingSpecification: FrontendSampleSpecification | undefined;
				let specificationWarning: boolean = false;

				if (specificationName && resultSample.matrixId) {
					const matchingSpecifications: Array<FrontendSampleSpecification> =
						organizationSpecifications.filter((spec) => {
							const fileDisplayName: string = getFileDisplayName(
								spec.fileName,
								spec.format,
							);
							return (
								fileDisplayName === specificationName &&
								spec.matrixId === resultSample.matrixId
							);
						});
					if (matchingSpecifications.length >= 1) {
						matchingSpecification = matchingSpecifications[0];
						if (matchingSpecifications.length > 1) specificationWarning = true;
					} else {
						//unable to find the spec file
						specificationWarning = true;
					}
				}

				//convert info into sample instance
				const resultInstance: FrontendSampleInstance = {
					sampleInstanceId: createUUID(),
					sample: resultSample,
					packageId: matchingPackage?.packageId || "",
					weight: valueToString(row[headerIndexMap.weight as number]),
					weightUnit: valueToString(
						row[headerIndexMap.weightUnit as number],
					) as WeightUnit,
					isRetest: isRetest,
					isAdditionalTest: isAdditionalTest,
					treatment: treatment,
					otherInstructions: otherInstructions,
					sampleTestInfo: {
						selectedTests: [], // populated later
						expectationInputs: {},
						additionalTest: otherTests ? true : false,
						additionalTestDescription: otherTests,
						turnaroundTime: valueToString(
							row[headerIndexMap.turnaroundTime as number],
						) as TurnaroundTime,
						status: TestStatus.SAMPLE_REGISTERED,
					},
					receivedTime: receivedTime,
					receivingCondition: receiveingCondition,
					expectedCompletionTime: new Date(
						valueToString(row[headerIndexMap.dueDate as number]),
					),
					pendingReview: false, //changed later
					multipleMatchingSamplesWarning: multipleMatchingSamplesWarning,

					specification: matchingSpecification,
					sampleInstanceAdditionalInfos: [],
				};

				//handle the instance additional fields
				// handle the instance additional fields
				Object.keys(additionalInstanceFieldHeaderMap).forEach((colIndex) => {
					const field: FrontendCompanySampleAdditionalInfoFields =
						additionalInstanceFieldHeaderMap[Number(colIndex)];

					const rawValue = valueToString(row[Number(colIndex)]);
					let finalValue: string | Date = rawValue;

					// Apply the same Date parsing logic as you did for Sample fields
					if (field.fieldType === AdditionalInfoFieldType.DATE && rawValue) {
						const parsedDate = new Date(rawValue);
						finalValue = isNaN(parsedDate.getTime()) ? "" : parsedDate;
					}

					resultInstance.sampleInstanceAdditionalInfos.push({
						fieldId: field.fieldId,
						infoType: field.infoType,
						additionalFieldName: field.additionalFieldName,
						fieldType: field.fieldType,
						value: finalValue,
					});
				});

				//validate mandatory sample additional infos
				for (const mandatoryField of instanceMandatoryAdditionalFields) {
					const existingEntry: FrontendSampleAdditionalInfoMapping | undefined =
						resultInstance.sampleInstanceAdditionalInfos.find(
							(info) => info.fieldId === mandatoryField.fieldId,
						);

					// 1. Check if the entry exists at all
					if (!existingEntry) {
						missingAdditionalInfoWarning = true;
						break;
					}

					const val = existingEntry.value;

					// 2. Validate based on type
					let isValueEmpty = false;

					if (val instanceof Date) {
						// Check if date is "Invalid Date"
						isValueEmpty = isNaN(val.getTime());
					} else if (typeof val === "string") {
						// Check if string is just whitespace or empty
						isValueEmpty = val.trim().length === 0;
					} else {
						// Handle null/undefined if your types allow it
						isValueEmpty = !val;
					}

					if (isValueEmpty) {
						missingAdditionalInfoWarning = true;
						break;
					}
				}

				resultInstance.pendingReview =
					multipleMatchingSamplesWarning ||
					specificationWarning ||
					!resultInstance.weight ||
					!resultInstance.weightUnit ||
					!resultInstance.receivingCondition ||
					isNaN(resultInstance.receivedTime?.getDate()) ||
					!resultInstance.expectedCompletionTime ||
					isNaN(resultInstance.expectedCompletionTime?.getDate()) ||
					missingAdditionalInfoWarning;

				let hasAnyTest: boolean = false;
				//handle the tests
				Object.keys(testHierarchyNodeHeaderMap).forEach((colIndex) => {
					const node: FrontendCompanyTestHierarchyNode =
						testHierarchyNodeHeaderMap[Number(colIndex)];
					const enabled: boolean = parseBoolean(
						valueToString(row[Number(colIndex)]),
					);
					if (enabled) {
						resultInstance.sampleTestInfo?.selectedTests.push({
							selectedNodeId: node.nodeId,
							assignedBatchId: undefined,
						});
						hasAnyTest = true;
					}
				});
				//at this point check if there are any tests enabled
				if (!hasAnyTest) {
					openSnackbar(
						translation(`Unable to find any tests for row: ${index + 2}`),
						Severity.ERROR,
						undefined,
						<RedErrorIcon />,
					);
					return;
				}

				//handle the test options
				Object.keys(hierarchyTestGroupNodeKeyAndChildrenMap).forEach(
					(colIndex) => {
						const childNodes: FrontendCompanyTestHierarchyNode[] =
							hierarchyTestGroupNodeKeyAndChildrenMap[Number(colIndex)];
						const selectedOptionText: string = valueToString(
							row[Number(colIndex)],
						);

						if (selectedOptionText) {
							const matchingOptionNode:
								| FrontendCompanyTestHierarchyNode
								| undefined = childNodes.find((node) => {
								return node.nodeKey == selectedOptionText;
							});
							if (matchingOptionNode) {
								resultInstance.sampleTestInfo?.selectedTests.push({
									selectedNodeId: matchingOptionNode.nodeId,
									assignedBatchId: undefined,
								});
							}
						}
					},
				);

				sampleInstances.push(resultInstance);
				index++;
			}

			setSelectedFile(file);

			return { newSamples, sampleInstances: sampleInstances };
		} catch (error: any) {
			console.error(error);
			setSelectedFile(null);

			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(true);
	};

	const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		event.stopPropagation();
		setDragging(false);
		const droppedFiles = event.dataTransfer.files;
		if (droppedFiles && droppedFiles.length > 0) {
			setSelectedFile(droppedFiles[0]);
		}
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const selectedFiles = event.target.files;
		if (selectedFiles && selectedFiles.length > 0) {
			setSelectedFile(selectedFiles[0]);
		}
	};

	const handleRemoveFile = () => {
		setSelectedFile(null);
	};

	const uploader: React.ReactNode = (
		<div>
			<Paper
				sx={{
					padding: 3,
					border: dragging
						? `2px dashed ${PrimaryColor.PRIMARY_LV5}`
						: `2px dashed ${NeutralColor.NEUTRAL_GREY_300}`,
					textAlign: "center",
					backgroundColor: "#F3F3F5",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					boxShadow: "none", // Add this line to remove the shadow
				}}
				onDragEnter={handleDragEnter}
				onDragOver={(e) => e.preventDefault()}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<div className="test-report-upload-icon-container">
					<UploadIcon></UploadIcon>
				</div>

				<input
					type="file"
					onChange={handleFileChange}
					style={{ display: "none" }}
					id="file-input"
					accept={ACCEPTED_EXTENSIONS.join(",")}
				/>
				<div style={{ marginTop: "10px" }}>
					{translation("DragDropFileDescription")}
					<label
						htmlFor="file-input"
						style={{ cursor: "pointer", marginTop: 10 }}
					>
						<div>
							<b>
								<u>{translation("chooseFile")}</u>
							</b>
						</div>
					</label>
				</div>
			</Paper>

			<div style={{ marginTop: 3, maxHeight: "500px", overflow: "auto" }}>
				{selectedFile && (
					<div className="upload-report-file-row">
						<div style={{ maxWidth: "80%", wordWrap: "break-word" }}>
							{selectedFile.name}
						</div>
						{!loading ? (
							<div
								id="delete-import-file"
								style={{ cursor: "pointer" }}
								onClick={handleRemoveFile}
							>
								<DeleteIcon></DeleteIcon>
							</div>
						) : (
							<GreenTickIcon
								size={20}
								strokeColor="white"
								fillColor={SemanticColor.SUCCESS}
							></GreenTickIcon>
						)}
					</div>
				)}
			</div>
		</div>
	);

	let currentPageConent: React.ReactNode = (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				gap: "10px",
				margin: "0px 20px",
			}}
		>
			{uploader}
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					marginTop: "12px",
				}}
			></div>
		</div>
	);

	const importOrderModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("ImportOrder")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<GreenButton
					key="start-import-button"
					id="start-import-button"
					children={
						loading ? (
							<ButtonTextLoadingAnimation
								text={translation("Loading")}
							></ButtonTextLoadingAnimation>
						) : (
							translation("Import")
						)
					}
					onClick={async () => {
						if (selectedFile) {
							const result:
								| {
										newSamples: Array<FrontendSample>;
										sampleInstances: Array<FrontendSampleInstance>;
								  }
								| undefined = await parseAndSetFile(selectedFile);
							if (result) {
								props.updateSampleInstancesFromImport(
									result.newSamples,
									result.sampleInstances,
								);

								openSnackbar(
									translation("OrderImported"),
									Severity.SUCCESS,
									undefined,
									<GreenTickIcon />,
								);
								setOpen(false);
							}
						}
					}}
					disabled={loading}
				></GreenButton>,
			]}
		>
			{currentPageConent}
		</ModalWithButtons>
	);
	return <>{importOrderModal}</>;
}
